import { ShieldCheck, ShieldAlert, ShieldX } from 'lucide-react';
import { useSCAFreshness } from '@/hooks/useSCAFreshness';
import { cn } from '@/lib/utils';

interface SCAFreshnessChipProps {
  storageKey: string;
  windowMs?: number;
  className?: string;
}

/**
 * Small status chip that shows SCA/step-up freshness.
 * Only renders when a step-up has been performed (status !== 'none').
 */
export function SCAFreshnessChip({ storageKey, windowMs, className }: SCAFreshnessChipProps) {
  const { status, secondsLeft } = useSCAFreshness(storageKey, windowMs);

  if (status === 'none') return null;

  if (status === 'fresh') {
    return (
      <div className={cn(
        'flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold',
        'bg-accent/10 text-accent border border-accent/20',
        className,
      )}>
        <ShieldCheck className="w-3 h-3" />
        <span>Auth verified</span>
      </div>
    );
  }

  if (status === 'expiring') {
    return (
      <div className={cn(
        'flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold animate-pulse',
        'bg-warning/10 text-warning border border-warning/20',
        className,
      )}>
        <ShieldAlert className="w-3 h-3" />
        <span>Re-auth in {secondsLeft}s</span>
      </div>
    );
  }

  // expired
  return (
    <div className={cn(
      'flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold',
      'bg-destructive/10 text-destructive border border-destructive/20',
      className,
    )}>
      <ShieldX className="w-3 h-3" />
      <span>Re-auth required</span>
    </div>
  );
}
